import { supabase } from './supabase';

export interface ProceedingFiling {
  id_submission: string;
  filers?: Array<{ name: string }>;
  date_received?: string;
  date_submission?: string;
  submissiontype?: { short: string; description?: string };
  filing_type?: string;
  express_comment?: number;
  /** True for ex-parte filings (ECFS 'NOTICE OF EXPARTE'). Present on the DB path. */
  is_ex_parte?: boolean;
  proceedings?: Array<{ name: string }>;
  text_data?: string;
  /** Pre-computed period bucket from the backend. Present when sourceMode='db'. */
  period_type?: 'comments' | 'reply_comments' | null;
  /** Pre-computed spam flag from the backend. Present when sourceMode='db'. */
  is_spam?: boolean;
  /** Storage paths from uploaded_documents. Present when sourceMode='db'. */
  document_paths?: Array<{
    file_name: string;
    storage_path: string;
    file_size: number | null;
    source_url: string | null;
  }>;
  document_count?: number;
}

export interface ProceedingTimelineBounds {
  comments_period_start?: string | null;
  comments_period_end?: string | null;
  reply_comments_period_end?: string | null;
}

export interface ProceedingFilingFetchMeta {
  sourceMode: 'bounded' | 'broad' | 'db';
  fallbackUsed: boolean;
  fallbackReason?: 'edge_day_missing' | 'db_empty';
  params: { startDate?: string; endDate?: string; documentLimit?: number };
}

export interface ProceedingFilingFetchOptions {
  allowBroadFallback?: boolean;
}

function formatDateForApi(dateInput: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) return dateInput;
  const d = new Date(dateInput);
  const y = d.getUTCFullYear();
  const m = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${d.getUTCDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  const y = d.getUTCFullYear();
  const m = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${d.getUTCDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isExpressFiling(filing: ProceedingFiling): boolean {
  const raw = (filing as { express_comment?: unknown }).express_comment;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw > 0;
  if (typeof raw === 'string') return raw === '1' || raw.toLowerCase() === 'true';
  return false;
}

function normalizeFilings(rawFilings: ProceedingFiling[], docketNumber: string): ProceedingFiling[] {
  const docketFiltered = rawFilings.filter((filing) => {
    if (isExpressFiling(filing)) return false;
    if (filing.proceedings && Array.isArray(filing.proceedings)) {
      return filing.proceedings.some(
        (proc: any) => proc.name && proc.name.trim() === docketNumber.trim()
      );
    }
    return filing.proceedings?.[0]?.name?.trim() === docketNumber.trim();
  });

  const deduped = new Map<string, ProceedingFiling>();
  for (const filing of docketFiltered) {
    const key = filing.id_submission;
    const existing = deduped.get(key);
    if (!existing) {
      deduped.set(key, filing);
      continue;
    }
    const existingDate = new Date(existing.date_received || existing.date_submission || '').getTime() || 0;
    const filingDate = new Date(filing.date_received || filing.date_submission || '').getTime() || 0;
    if (filingDate > existingDate) deduped.set(key, filing);
  }

  return Array.from(deduped.values())
    .map((f) => ({
      // DB path sets is_ex_parte authoritatively; on the ECFS path derive it
      // from the submission type description ('NOTICE OF EXPARTE').
      ...f,
      is_ex_parte: f.is_ex_parte ?? /ex\s*parte/i.test(f.submissiontype?.description || ''),
    }))
    .sort((a, b) => {
      const dateA = new Date(a.date_received || a.date_submission || '').getTime() || 0;
      const dateB = new Date(b.date_received || b.date_submission || '').getTime() || 0;
      return dateB - dateA;
    });
}

async function callSearchDocket(
  docketNumber: string,
  params: { startDate?: string; endDate?: string; documentLimit?: number }
): Promise<ProceedingFiling[]> {
  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-docket`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      docketNumber,
      ...params,
    }),
  });
  if (!response.ok) throw new Error(`Failed to fetch filings: ${response.status}`);
  const data = await response.json();
  return (data?.filing || []) as ProceedingFiling[];
}

interface DbFilingRow {
  id_submission: string;
  filer_name: string | null;
  filers: Array<{ name: string }> | null;
  date_received: string | null;
  date_submission: string | null;
  submissiontype: { short?: string; description?: string } | null;
  filing_type: string | null;
  express_comment: boolean | null;
  period_type: 'comments' | 'reply_comments' | 'other' | null;
  document_count: number | null;
  document_paths: Array<{
    file_name: string;
    storage_path: string;
    file_size: number | null;
    source_url: string | null;
  }> | null;
  is_spam: boolean | null;
  is_ex_parte: boolean | null;
  text_status: string | null;
  has_text: boolean | null;
}

async function fetchFilingsFromDb(
  docketNumber: string,
  documentLimit: number
): Promise<ProceedingFiling[] | null> {
  try {
    const { data, error } = await supabase.rpc('get_proceeding_filings', {
      p_docket_number: docketNumber,
      p_period_type: null,
      p_exclude_spam: true,
      p_exclude_express: true,
      p_limit: documentLimit,
    });
    if (error) {
      console.warn('[fetchFilingsFromDb] rpc error, falling back to ECFS:', error.message);
      return null;
    }
    const rows = (data as DbFilingRow[] | null) ?? [];
    if (rows.length === 0) return null;
    return rows.map((r): ProceedingFiling => ({
      id_submission: r.id_submission,
      filers: r.filers ?? (r.filer_name ? [{ name: r.filer_name }] : []),
      date_received: r.date_received ?? undefined,
      date_submission: r.date_submission ?? undefined,
      submissiontype: r.submissiontype?.short
        ? { short: r.submissiontype.short, description: r.submissiontype.description }
        : r.filing_type
          ? { short: r.filing_type }
          : undefined,
      filing_type: r.filing_type ?? undefined,
      express_comment: r.express_comment ? 1 : 0,
      is_ex_parte: r.is_ex_parte ?? false,
      period_type: r.period_type === 'other' ? null : r.period_type,
      is_spam: r.is_spam ?? undefined,
      document_paths: r.document_paths ?? undefined,
      document_count: r.document_count ?? undefined,
    }));
  } catch (err) {
    console.warn('[fetchFilingsFromDb] exception, falling back to ECFS:', err);
    return null;
  }
}

export async function fetchProceedingsFilings(
  docketNumber: string,
  timeline?: ProceedingTimelineBounds | null,
  documentLimit = 200,
  options: ProceedingFilingFetchOptions = {}
): Promise<{ filings: ProceedingFiling[]; meta: ProceedingFilingFetchMeta }> {
  // Prefer the DB-backed path: filings.* is populated by ingest-worker and
  // pre-classified (period_type, is_spam) via triggers. Avoids the slow
  // ECFS round-trip + the per-call spam-filer batch check the caller would
  // otherwise have to run. Falls back to ECFS when the docket isn't in the
  // table yet (cold ingest) or the RPC errors.
  const dbFilings = await fetchFilingsFromDb(docketNumber, documentLimit);
  if (dbFilings && dbFilings.length > 0) {
    return {
      filings: dbFilings,
      meta: { sourceMode: 'db', fallbackUsed: false, params: { documentLimit } },
    };
  }

  const { allowBroadFallback = true } = options;
  const broadParams = {
    startDate: '1992-01-01',
    endDate: new Date().toISOString().split('T')[0],
  };

  const commentsStart = timeline?.comments_period_start ? formatDateForApi(timeline.comments_period_start) : null;
  const replyEnd = timeline?.reply_comments_period_end
    ? formatDateForApi(timeline.reply_comments_period_end)
    : timeline?.comments_period_end
      ? formatDateForApi(timeline.comments_period_end)
      : null;

  if (!commentsStart || !replyEnd) {
    const broadRaw = await callSearchDocket(docketNumber, broadParams);
    return {
      filings: normalizeFilings(broadRaw, docketNumber),
      meta: { sourceMode: 'broad', fallbackUsed: false, params: broadParams },
    };
  }

  const boundedParams = {
    startDate: commentsStart,
    endDate: addDays(replyEnd, 1),
  };
  const boundedRaw = await callSearchDocket(docketNumber, boundedParams);
  const bounded = normalizeFilings(boundedRaw, docketNumber);

  // Conditional fallback: if no filings are on/after reply end date, verify via broad pull once.
  const hasReplyEdgeDay = bounded.some((f) => {
    const d = f.date_submission || f.date_received;
    return !!d && formatDateForApi(d) >= replyEnd;
  });

  if (allowBroadFallback && !hasReplyEdgeDay) {
    const broadRaw = await callSearchDocket(docketNumber, broadParams);
    const broad = normalizeFilings(broadRaw, docketNumber);
    const broadHasReplyEdgeDay = broad.some((f) => {
      const d = f.date_submission || f.date_received;
      return !!d && formatDateForApi(d) >= replyEnd;
    });
    if (broadHasReplyEdgeDay && broad.length >= bounded.length) {
      return {
        filings: broad,
        meta: {
          sourceMode: 'broad',
          fallbackUsed: true,
          fallbackReason: 'edge_day_missing',
          params: broadParams,
        },
      };
    }
  }

  return {
    filings: bounded,
    meta: { sourceMode: 'bounded', fallbackUsed: false, params: boundedParams },
  };
}
